import React, { useState, useMemo } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import { Avatar, ReviewRatingCustom, UserDisplayName, IconButtonArrow } from '../../components';

import css from './Reviews.module.css';

const MAX_LENGTH = 180;
const INITIAL_VISIBLE = 3;

export const getRelativeTime = (date, locale, intl) => {
  const now = Date.now();
  const diff = now - date;

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);


  const getTranslation = (key, count = 1) => {
    return intl.formatMessage({ id: `Reviews.relativeTime.${key}` }, { count });
  };

  const getTimeKey = (unit, value) => {
    if (value === 1) {
      return `${unit}Ago`;
    }
    return `${unit}sAgo`;
  };

  if (seconds < 60) {
    return getTranslation(getTimeKey('second', seconds), seconds);
  }

  if (minutes < 60) {
    return getTranslation(getTimeKey('minute', minutes), minutes);
  }

  if (hours < 24) {
    return getTranslation(getTimeKey('hour', hours), hours);
  }

  if (days < 30) {
    return getTranslation(getTimeKey('day', days), days);
  }

  if (months < 12) {
    return getTranslation(getTimeKey('month', months), months);
  }

  if (years === 1) {
    return getTranslation('yearAgo');
  }

  if (years > 1) {
    return getTranslation('yearsAgo', years);
  }

  return getTranslation('overYearAgo');
};

const Review = props => {
  const { review, intl, isPlaceReview, locale } = props;
  const [expanded, setExpanded] = useState(false);

  const date = !isPlaceReview
    ? review.attributes.createdAt
    : Number(review.publishTime.seconds * 1000);

  const dateString = getRelativeTime(date, locale, intl);

  const content = !isPlaceReview
    ? review.attributes.content
    : review.text;

  const isLong = content?.length > MAX_LENGTH;

  const displayedText = useMemo(() => {
    if (expanded || !isLong) return content;
    return content.slice(0, MAX_LENGTH) + '...';
  }, [expanded, content, isLong]);

  const authorCountry = review?.author?.attributes?.profile?.publicData?.userField_countrytourist;

  return (
    <div className={css.review}>
      <div className={css.reviewAuthor}>
        {!isPlaceReview ? (
          <Avatar className={css.avatar} user={review.author} />
        ) : (
          <a href={review.authorUri} target="_blank" rel="noopener noreferrer">
            <img
              className={css.placeReviewAvatar}
              src={review.authorUrl}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          </a>
        )}

        <div className={css.reviewAuthorName}>
          {!isPlaceReview ? (
            <UserDisplayName user={review.author} intl={intl} />
          ) : (
            <span>{review.author}</span>
          )}
          {authorCountry && (
            <span className={css.reviewAuthorCountry}>
              {authorCountry}
            </span>
          )}
        </div>
      </div>

      <div className={css.reviewRating}>
        <div className={css.reviewRatingItem}>

          <ReviewRatingCustom
            rating={!isPlaceReview ? review.attributes.rating : review.rating}
            className={css.ratingStars}
          />

          <span className={css.reviewRatingTotal}>
            {!isPlaceReview
              ? review.attributes.rating.toFixed(1)
              : review.rating.toFixed(1)}
          </span>
        </div>

        <div className={css.reviewRatingItem}>
          <span className={css.reviewRatingDay}>
            {dateString}
          </span>
        </div>
      </div>

      <p className={css.reviewContent}>
        {displayedText}
      </p>

      {isLong && (
        <span
          className={css.readMore}
          onClick={() => setExpanded(prev => !prev)}
        >
          <IconButtonArrow />
          {expanded ? intl.formatMessage({ id: 'ListingPage.readLess' }) :
            intl.formatMessage({ id: 'ListingPage.readMore' })}
        </span>
      )}
    </div>
  );
};

/**
 * A component that renders a list of reviews.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.review>} props.reviews - The reviews to render
 * @returns {JSX.Element}
 */
const Reviews = props => {
  const intl = useIntl();
  const { className, rootClassName, reviews = [], isPlaceReview = false, locale } = props;
  const classes = classNames(rootClassName || css.root, className);

  const [showAll, setShowAll] = useState(false);

  if (!reviews?.length) return null;

  const visibleReviews = showAll
    ? reviews
    : reviews.slice(0, INITIAL_VISIBLE);

  return (
    <div>
      <ul className={classes}>
        {visibleReviews.map((r, index) => (
          <li
            key={`Review_${!isPlaceReview ? r.id.uuid : index}`}
            className={css.reviewItem}
          >
            <Review
              review={r}
              intl={intl}
              isPlaceReview={isPlaceReview}
              locale={locale}
            />
          </li>
        ))}
      </ul>

      {reviews?.length > INITIAL_VISIBLE && (
        <div className={css.viewAllWrapper}>
          <button
            type="button"
            className={css.viewAllButton}
            onClick={() => setShowAll(prev => !prev)}
          >
            {showAll ? intl.formatMessage({ id: 'ListingPage.showLess' }) :
              intl.formatMessage({ id: 'ListingPage.viewAll' })}
          </button>
        </div>
      )}
    </div>
  );
};

export default Reviews;
